import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import Toast, { ToastVariant } from '@/components/toast';
import useUpdatedRef from '@/hooks/useUpdatedRef';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

export type ToastContextType = {
  toastContent: ReactNode;
  setToastContent: (val: ReactNode, persist?: boolean) => void;
  clearToastContent: () => void;
  showErrorToast: (message: string) => void;
  showSuccessToast: (title: string, subtitle?: string) => void;
  toastPosition: 'bottom' | 'top';
};

export const ToastContext = React.createContext<ToastContextType>({
  clearToastContent: unimplementedFunction,
  setToastContent: unimplementedFunction,
  showErrorToast: unimplementedFunction,
  showSuccessToast: unimplementedFunction,
  toastContent: null,
  toastPosition: 'bottom',
});

type Props = PropsWithChildren<{
  toastPosition?: 'bottom' | 'top';
}>;

export const ToastProvider: React.FC<Props> = ({
  children = undefined,
  toastPosition = 'bottom',
}) => {
  const [toastContent, toastContentSet] = useState<ReactNode>(null);

  /** Timeout id is used to handle toasts automatically closing after some time
   *  - When toast content get cleared, the timeoutId is reset so that it doesn't erronously call clearToastContent
   *  - When new toast content is set, the previous timeout get cleared so that the old timeout doesn close out the new modal
   */
  const [timeoutId, setTimeoutId] = useState<number | null>(null);

  const timeoutRef = useUpdatedRef(timeoutId);

  /**
   * Clears the current timeout if there is a timeoutId and reset the state (i.e. cancel the timer for auto-closing)
   */
  const clearCurrentTimeout = useCallback(() => {
    if (timeoutRef.current === null) return;
    clearTimeout(timeoutRef.current);
    setTimeoutId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *  Clears toast content and clears the timeout
   */
  const clearToastContent = useCallback(() => {
    toastContentSet(null);

    clearCurrentTimeout();
  }, [clearCurrentTimeout]);

  /**
   * Set the content of the toast to the provided ReactNode
   *  - Cancels any active timeouts, and starts a new one for autoclosing
   */
  const setToastContent = useCallback(
    (val: ReactNode, persist = false) => {
      // If there is a
      clearCurrentTimeout();

      if (!persist) {
        const tid = setTimeout(clearToastContent, 5000);
        setTimeoutId(tid as any);
      }

      toastContentSet(val);
    },
    [clearCurrentTimeout, clearToastContent]
  );

  const showErrorToast = useCallback(
    (message: string) => {
      setToastContent(
        <Toast subtitle={message} title="Error" variant={ToastVariant.ERROR} />
      );
    },
    [setToastContent]
  );

  const showSuccessToast = useCallback(
    (title: string, subtitle?: string) => {
      setToastContent(
        <Toast
          subtitle={subtitle}
          title={title}
          variant={ToastVariant.SUCCESS}
        />
      );
    },
    [setToastContent]
  );

  const value: ToastContextType = useMemo(() => {
    return {
      clearToastContent,
      setToastContent,
      showErrorToast,
      showSuccessToast,
      toastContent,
      toastPosition,
    };
  }, [
    clearToastContent,
    toastPosition,
    setToastContent,
    showErrorToast,
    toastContent,
    showSuccessToast,
  ]);

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};

/** Hook for easily accessing toast context
 *   - To display a toast simply, set the toast content to you desired JSX. ex: setToastContent(<>toast jsx here</>)
 *   - To close the current toast, just set the toast content to null. ex: setToastContent(null)
 */
export const useToastContext = (): ToastContextType => {
  const {
    setToastContent,
    toastContent,
    clearToastContent,
    toastPosition,
    showErrorToast,
    showSuccessToast,
  } = useContext(ToastContext);

  return {
    clearToastContent,
    setToastContent,
    showErrorToast,
    showSuccessToast,
    toastContent,
    toastPosition,
  };
};
